import React, { useEffect, useRef, useState } from "react";
import { Copy, FileUp, Loader2, Trash2, Upload } from "lucide-react";
import { api } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls = "admin-input w-full bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm";
const fmtSize = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(2)} MB` : n >= 1024 ? `${(n / 1024).toFixed(1)} KB` : `${n} B`);
const absUrl = (u) => (u.startsWith("/") ? `${API}${u}` : u);

function FileRow({ f, onDelete, busy }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(absUrl(f.url)); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch { /* clipboard blocked */ } };
  return (
    <div className="flex items-center gap-3 px-3 py-2 text-xs" data-testid={`root-file-row-${f.name}`}>
      <a href={absUrl(f.url)} target="_blank" rel="noreferrer" className="mono text-zinc-200 hover:text-white truncate flex-1" data-testid={`root-file-link-${f.name}`}>/{f.name}</a>
      <span className="text-zinc-500 mono hidden sm:inline truncate max-w-[160px]">{f.content_type.split(";")[0]}</span>
      <span className="text-zinc-500 mono w-16 text-right">{fmtSize(f.size)}</span>
      <button data-testid={`root-file-copy-${f.name}`} onClick={copy} title="Copy public URL" className="text-zinc-400 hover:text-white"><Copy size={13} /></button>
      <button data-testid={`root-file-delete-${f.name}`} onClick={() => onDelete(f.name)} disabled={busy} title="Delete" className="text-rose-400 hover:text-rose-300 disabled:opacity-40"><Trash2 size={13} /></button>
      {copied && <span className="text-emerald-400 mono">copied</span>}
    </div>
  );
}

function AddFile({ site, limits, onAdded }) {
  const [mode, setMode] = useState("upload");
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);
  const canSubmit = mode === "upload" ? !!file : (name.trim() && content.trim());
  async function submit() {
    setBusy(true); setErr("");
    try {
      let res;
      if (mode === "upload") {
        const fd = new FormData();
        fd.append("file", file);
        if (name.trim()) fd.append("name", name.trim());
        res = await api.post(`/sites/${site.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        res = await api.post(`/sites/${site.id}/files/text`, { name: name.trim(), content });
      }
      onAdded(res.data);
      setName(""); setContent(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) { setErr(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  }
  return (
    <div className="border border-zinc-800 rounded-sm p-3 space-y-3 bg-zinc-950/40" data-testid="root-file-add">
      <div className="flex items-center gap-4 text-xs">
        <label className="flex items-center gap-1.5 cursor-pointer"><input data-testid="root-file-mode-upload" type="radio" checked={mode === "upload"} onChange={() => setMode("upload")} /> Upload a file</label>
        <label className="flex items-center gap-1.5 cursor-pointer"><input data-testid="root-file-mode-text" type="radio" checked={mode === "text"} onChange={() => setMode("text")} /> Paste text</label>
      </div>
      {mode === "upload" ? (
        <label className="flex items-center gap-3 border border-dashed border-zinc-700 hover:border-zinc-500 rounded-sm px-3 py-3 cursor-pointer text-xs text-zinc-300">
          <FileUp size={14} className="text-zinc-500 shrink-0" />
          <span className="truncate flex-1">{file ? `${file.name} · ${fmtSize(file.size)}` : "Choose a file (html, txt, xml, json, js, css, pdf, images — max 2 MB)"}</span>
          <input ref={fileRef} data-testid="root-file-input" type="file" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setErr(""); }} />
        </label>
      ) : (
        <textarea data-testid="root-file-content" value={content} onChange={(e) => setContent(e.target.value)} rows={4} spellCheck={false}
                  placeholder="google-site-verification: google1234abcd.html" className={`${inputCls} text-xs mono`} />
      )}
      <div className="flex items-center gap-2">
        <input data-testid="root-file-name" value={name} onChange={(e) => setName(e.target.value)}
               placeholder={mode === "upload" ? "Save as (optional, e.g. favicon.ico)" : "Filename, e.g. ads.txt or .well-known/security.txt"} className={`${inputCls} mono text-xs`} />
        <button data-testid="root-file-submit" onClick={submit} disabled={busy || !canSubmit}
                className="inline-flex items-center gap-1.5 shrink-0 bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-40 px-3 py-2 rounded-sm text-xs font-semibold">
          {busy ? <Loader2 className="animate-spin" size={12} /> : <Upload size={12} />} Add
        </button>
      </div>
      {err && <div data-testid="root-file-error" className="text-xs text-rose-400">{err}</div>}
      <div className="text-[11px] text-zinc-500">
        Served at <span className="mono text-zinc-300">https://{site.domain || "{domain}"}/&lt;name&gt;</span> — root or <span className="mono">.well-known/</span> only, {limits?.max_files || 25} files max.
        <span className="text-amber-300/80"> .php files are not uploads: every <span className="mono">/*.php</span> URL already acts as the email-open pixel relay (section above).</span>
      </div>
    </div>
  );
}

/** Settings → Root files: verification files, ads.txt, favicon, .well-known/* */
export default function RootFilesPanel({ site }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = async () => { try { setData((await api.get(`/sites/${site.id}/files`)).data); } catch { /* shown empty */ } };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site.id]);
  async function del(name) {
    if (!window.confirm(`Delete /${name}? It stops being served immediately.`)) return;
    setBusy(true); setMsg("");
    try { await api.delete(`/sites/${site.id}/files/${name}`); setMsg(`Deleted /${name}`); await load(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  }
  const files = data?.files || [];
  return (
    <div className="space-y-3" data-testid="root-files-panel">
      {files.length ? (
        <div className="border border-zinc-800 rounded-sm divide-y divide-zinc-800" data-testid="root-files-list">
          {files.map((f) => <FileRow key={f.name} f={f} onDelete={del} busy={busy} />)}
        </div>
      ) : (
        <div className="text-xs text-zinc-500" data-testid="root-files-empty">No root files yet.</div>
      )}
      {msg && <div data-testid="root-files-msg" className="text-xs mono text-zinc-300">{msg}</div>}
      <AddFile site={site} limits={data?.limits} onAdded={(row) => { setMsg(`${row.replaced ? "Replaced" : "Added"} /${row.name}`); load(); }} />
    </div>
  );
}
